import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { sanityClient, WORKFLOW_MANIFEST_QUERY } from "../sanity";
import { useWorkflowVariant } from "../growthbook";
import { getCachedManifest } from "./cached-config";
import { activeManifestChanged } from "./manifest-capabilities";
import type { WorkflowManifest } from "../../types/workflow-schema";

// Module-level so placeholderData keeps a stable identity: a fresh object per
// render would cascade into use-dynamic-steps.ts's memoized categorySteps/
// current/lastReachedStep recomputing every render and could retrigger
// navigation effects in a loop.
const CACHED_MANIFEST = getCachedManifest();

/**
 * Fetches the lightweight workflow manifest from Sanity.
 * Contains step ordering, paths, and componentKeys per category — no field schemas.
 *
 * - Fetched once per session (staleTime: Infinity)
 * - Falls back to build-time cached manifest if Sanity is unreachable
 *
 * The "availability" (OSE) step is deliberately NOT patched in here: it must
 * come from the manifest itself (the `on-site-estimate` Sanity variant, or
 * the build-time cache). Its presence is mirrored into $manifestCapabilities,
 * which the Moving contracts + branching-rules read to decide between the
 * OSE self-scheduling flow and the old call flow — patching the step in
 * unconditionally would make that check always-true and route control-bucket
 * users onto a page whose step-config content doesn't exist.
 */
export const useWorkflowManifest = () => {
  const variantId = useWorkflowVariant();

  const { data, isLoading, isPlaceholderData, error } =
    useQuery<WorkflowManifest>({
      queryKey: ["workflow-manifest", variantId],
      queryFn: async () => {
        let result = await sanityClient.fetch<WorkflowManifest | null>(
          WORKFLOW_MANIFEST_QUERY,
          { variantId },
        );

        // Unknown / misconfigured variant (e.g. a typo or a deleted variant
        // returned by GrowthBook). Coalesce to the live "default" workflow
        // before resorting to the build-time cached snapshot.
        if (!result && variantId !== "default") {
          result = await sanityClient.fetch<WorkflowManifest | null>(
            WORKFLOW_MANIFEST_QUERY,
            { variantId: "default" },
          );
        }

        if (!result) {
          // Sanity unreachable or no "default" workflow — use build-time cache
          return CACHED_MANIFEST;
        }

        return result;
      },
      staleTime: Infinity, // Never re-fetch during a booking session
      gcTime: Infinity,
      retry: 1,
      // If Sanity fetch fails, fall back to cached config
      placeholderData: CACHED_MANIFEST,
    });

  const manifest = data ?? CACHED_MANIFEST;

  // Mirror the ACTIVE manifest into $manifestCapabilities so non-React code
  // (api-contracts conditions, branching-rules) can read what the router is
  // actually working with.
  useEffect(() => {
    activeManifestChanged(manifest);
  }, [manifest]);

  // TEMP DEBUG: confirm which variant + manifest is actually resolving.
  // Remove once the variant-flip investigation is closed out.
  useEffect(() => {
    if (!data) return;
    console.log(
      "[useWorkflowManifest] variant:",
      variantId,
      "| source:",
      isPlaceholderData ? "placeholder (build-time cache)" : "sanity",
      "| Moving steps:",
      data.serviceCategories?.Moving?.map((s) => ({
        stepId: s.stepId,
        tracks: s.tracks,
      })),
    );
  }, [variantId, data, isPlaceholderData]);

  return {
    manifest,
    isLoading,
    // True once the real Sanity data has arrived (not placeholder/build-time cache).
    // Use this to suppress guards that depend on manifest completeness.
    isManifestReady: !isPlaceholderData && data != null,
    error,
  };
};
